import React, { Fragment } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  LISTING_ATTRIBUTE_SECTIONS,
  createEmptyListingAttributes
} from '../config/listingAttributesModel';
import { PawFilterRow } from './PawFilterRow';
import { FilterSectionBoneIcon } from './FilterSectionBoneIcon';
import { FilterDashedSeparator } from './FilterDashedSeparator';
import { ChipPaw, filterChipBtn } from './searchFilterChips';

/**
 * Secondary filters for search — same keys as stay.listingAttributes.
 * Dashed line only between major sections (category separators), not between rows within a section.
 * @param {boolean} [embedded] When true, omit leading separator (parent already separates from primary filters).
 * @param {string} [headingBoneClassName] Passed to `FilterSectionBoneIcon` (e.g. `shrink-0 text-black` in filter modal).
 * @param {boolean} [useModalChipStyle] Match FilterModal: section titles 18px medium + bone; consecutive yes/no + multiselect options use `flex flex-wrap gap-[4px]` like Property type; selects full-width pill borders.
 */
const ListingAttributesFilterForm = ({
  secondary,
  onChange,
  embedded = false,
  headingBoneClassName,
  useModalChipStyle = false
}) => {
  const s = { ...createEmptyListingAttributes(), ...(secondary || {}) };

  const patch = (key, v) => onChange({ ...s, [key]: v });

  const toggleMulti = (key, opt) => {
    const cur = Array.isArray(s[key]) ? [...s[key]] : [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    patch(key, next);
  };

  const sepClass = useModalChipStyle ? 'my-6' : 'my-[20px]';

  /** Single yes/no chip (used inside a shared `flex flex-wrap gap-[4px]` row like Property type). */
  const renderModalYesnoChip = (field) => {
    const sel = s[field.key] === 'Yes';
    return (
      <button
        key={field.key}
        type="button"
        aria-pressed={sel}
        aria-label={field.label}
        onClick={() => patch(field.key, sel ? null : 'Yes')}
        className={`${filterChipBtn(sel)} p-[10px] text-[13px] font-semibold leading-tight`}
      >
        <ChipPaw selected={sel} />
        <span>{field.label}</span>
      </button>
    );
  };

  const renderField = (field) => {
    if (useModalChipStyle && field.type === 'multiselect') {
      return (
        <>
          <p className="text-[12px] text-[#6b7280]">{field.label}</p>
          <div className="mt-2 flex flex-wrap gap-[4px]">
            {field.options.map((o) => {
              const sel = (s[field.key] || []).includes(o);
              return (
                <button
                  key={o}
                  type="button"
                  aria-pressed={sel}
                  aria-label={o}
                  onClick={() => toggleMulti(field.key, o)}
                  className={`${filterChipBtn(sel)} p-[10px] text-[13px] font-semibold leading-tight`}
                >
                  <ChipPaw selected={sel} />
                  <span>{o}</span>
                </button>
              );
            })}
          </div>
        </>
      );
    }

    if (useModalChipStyle && field.type === 'select') {
      const selected = (s[field.key] ?? '') !== '';
      return (
        <>
          <p className="text-[12px] text-[#6b7280]">{field.label}</p>
          <div className="relative mt-2">
            <select
              value={s[field.key] ?? ''}
              onChange={(e) => patch(field.key, e.target.value === '' ? null : e.target.value)}
              aria-label={field.label}
              className={`w-full cursor-pointer appearance-none rounded-full border bg-white py-[10px] pl-4 pr-12 text-left text-[13px] font-semibold leading-tight outline-none ring-offset-2 transition-colors focus-visible:ring-2 focus-visible:ring-black/20 ${
                selected ? 'border-black text-black' : 'border-[#E5E3DF] text-[#9B9B9B]'
              }`}
            >
              <option value="">Any</option>
              {field.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <ChevronDown
              className={`pointer-events-none absolute right-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 shrink-0 ${
                selected ? 'text-black' : 'text-[#9B9B9B]'
              }`}
              strokeWidth={2}
              aria-hidden
            />
          </div>
        </>
      );
    }

    if (field.type === 'yesno') {
      if (useModalChipStyle) return null;
      return (
        <PawFilterRow
          selected={s[field.key] === 'Yes'}
          blueAccent
          aria-pressed={s[field.key] === 'Yes'}
          aria-label={field.label}
          onClick={() => patch(field.key, s[field.key] === 'Yes' ? null : 'Yes')}
        >
          <span
            className={`text-[13px] font-regular leading-snug ${
              s[field.key] === 'Yes' ? 'text-[#3b82f6]' : 'text-black'
            }`}
          >
            {field.label}
          </span>
        </PawFilterRow>
      );
    }

    if (field.type === 'select') {
      return (
        <>
          <p className="text-[12px] text-[#6b7280]">{field.label}</p>
          <select
            value={s[field.key] ?? ''}
            onChange={(e) => patch(field.key, e.target.value === '' ? null : e.target.value)}
            className="w-full rounded-lg border border-[#e3e3e3] bg-white px-2 py-1.5 text-[13px]"
          >
            <option value="">Any</option>
            {field.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </>
      );
    }

    if (field.type === 'multiselect') {
      return (
        <>
          <p className="mb-2 text-[12px] text-[#6b7280]">{field.label}</p>
          <div className="flex flex-col gap-[8px]">
            {field.options.map((o) => {
              const sel = (s[field.key] || []).includes(o);
              return (
                <PawFilterRow
                  key={o}
                  selected={sel}
                  blueAccent
                  aria-pressed={sel}
                  aria-label={o}
                  onClick={() => toggleMulti(field.key, o)}
                >
                  <span
                    className={`text-[13px] font-regular leading-snug ${sel ? 'text-[#3b82f6]' : 'text-black'}`}
                  >
                    {o}
                  </span>
                </PawFilterRow>
              );
            })}
          </div>
        </>
      );
    }

    if (field.type === 'number') {
      return (
        <div className="flex items-center gap-2">
          {field.key === 'distanceFromCityKm' ? (
            <>
              <span className="text-[12px] text-[#6b7280]">Max km</span>
              <input
                type="number"
                min={field.min}
                max={field.max}
                value={s[field.key] ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  patch(field.key, v === '' ? null : Number(v));
                }}
                className="w-24 rounded-lg border border-[#e3e3e3] bg-white px-2 py-1 text-[13px]"
                placeholder="—"
              />
            </>
          ) : (
            <>
              <span className="text-[12px] text-[#6b7280]">Min</span>
              <input
                type="number"
                min={field.min}
                max={field.max}
                value={s[field.key] ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  patch(field.key, v === '' ? null : Number(v));
                }}
                className="w-20 rounded-lg border border-[#e3e3e3] bg-white px-2 py-1 text-[13px]"
                placeholder="—"
              />
            </>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={embedded ? '' : ''}>
      {!embedded && <FilterDashedSeparator className={sepClass} />}
      {LISTING_ATTRIBUTE_SECTIONS.map((section, sectionIndex) => (
        <Fragment key={section.id}>
          {sectionIndex > 0 && <FilterDashedSeparator className={sepClass} />}
          <div
            className={
              embedded
                ? sectionIndex === 0
                  ? 'space-y-3 '
                  : 'space-y-3 '
                : sectionIndex === 0
                  ? 'space-y-3  pt-0'
                  : 'space-y-3 '
            }
          >
            <div
              className={
                useModalChipStyle ? 'mb-4 flex items-center gap-2' : 'mb-3 flex items-center gap-2'
              }
            >
              <h3
                className={
                  useModalChipStyle
                    ? 'text-[18px] font-medium text-black'
                    : 'text-[14px] font-regular text-black'
                }
              >
                {section.title}
              </h3>
              <FilterSectionBoneIcon size={20} className={headingBoneClassName} />
            </div>
            <div className={`pl-0.5 ${useModalChipStyle ? 'space-y-4' : 'space-y-3'}`}>
              {useModalChipStyle
                ? (() => {
                    const blocks = [];
                    const fields = section.fields;
                    let i = 0;
                    while (i < fields.length) {
                      const f = fields[i];
                      if (f.type === 'yesno') {
                        const batch = [];
                        while (i < fields.length && fields[i].type === 'yesno') {
                          batch.push(fields[i]);
                          i++;
                        }
                        blocks.push(
                          <div
                            key={`yesno-wrap-${batch.map((x) => x.key).join('-')}`}
                            className="flex flex-wrap gap-[4px]"
                          >
                            {batch.map((bf) => renderModalYesnoChip(bf))}
                          </div>
                        );
                      } else {
                        blocks.push(
                          <div key={f.key} className="space-y-1.5">
                            {renderField(f)}
                          </div>
                        );
                        i++;
                      }
                    }
                    return blocks;
                  })()
                : section.fields.map((field) => (
                    <div key={field.key} className="space-y-1.5">
                      {renderField(field)}
                    </div>
                  ))}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
};

export default ListingAttributesFilterForm;
